<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { RoleDataPayload, RoleDataResponse } from "@/types/interface/role";
import { onMounted, ref } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import { useRoleStore } from "@/stores/master/role";
import { useRoleGroupStore } from "@/stores/master/role-group";
import { mapOptionMultiselect } from "@/utils/helper";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

const idRole = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const roleStore = useRoleStore();
const roleGroupStore = useRoleGroupStore();
const detailRole = ref<RoleDataResponse>();
const listRoleGroup = ref<any>();

const roleGroupSchema = zod
  .object({
    value: zod.number(),
    label: zod.string(),
  })
  .refine(
    (data) =>
      listRoleGroup.value.some(
        (code) => code.value === data.value && code.label === data.label
      ),
    {
      message: "Invalid port selected",
    }
  );

const validationSchema = toTypedSchema(
  zod.object({
    code: zod.string().nonempty({ message: "Code is required" }),
    role: zod.string().nonempty({ message: "Role is required" }),
    role_group_id: roleGroupSchema,
    description: zod.string().nonempty({ message: "Description is required" }),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: code } = useField<string>("code");
const { value: role } = useField<string>("role");
const { value: role_group_id } = useField<string>("role_group_id");
const { value: description } = useField<string>("description");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;
  const payloadRole: RoleDataPayload = {
    name: role.value,
    code: code.value,
    role_group_id: role_group_id.value?.value,
    description: description.value,
  };

  const responseApi = ref<any>();

  if (isEdit.value == true) {
    responseApi.value = await roleStore.dispatchUpdate(
      idRole.value,
      payloadRole
    );
  } else {
    responseApi.value = await roleStore.dispatchCreate(payloadRole);
  }

  const { success, message } = responseApi.value;
  if (success == true) {
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value ? "Role Successfully Updated" : "Role Successfully Added"
      );
      router.push("/master/role");
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadDetailRole = async () => {
  const { success, message } = await roleStore.dispatchDetail(idRole.value);
  if (success) {
    detailRole.value = await roleStore?.role?.data;
    role.value = detailRole.value?.name;
    code.value = detailRole.value?.code;
    role_group_id.value = mapOptionMultiselect(
      detailRole.value?.roleGroup,
      "id",
      "name"
    );
    description.value = detailRole.value?.description ?? "default";
  }
};

const loadListRoleGroup = async () => {
  const { success, message } = await roleGroupStore.dispatchAll(1, 99);
  if (success) {
    listRoleGroup.value = mapOptionMultiselect(
      await roleGroupStore.roleGroups.items,
      "id",
      "name"
    );
  }
};

onMounted(() => {
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idRole.value = router.currentRoute.value.params.id.toString();
    loadDetailRole();
  }
  loadListRoleGroup();
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create Role</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/master/role"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List Role
            </a>
          </div>
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading"
          >
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="code" class="form-label label text-secondary"
                    >Code <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="code"
                      v-model="code"
                      placeholder="Role Code"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.code }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="role" class="form-label label text-secondary"
                    >Role <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="role"
                      v-model="role"
                      placeholder="Role"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.role }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="description"
                    class="form-label label text-secondary"
                    >Description</label
                  >
                  <div class="position-relative">
                    <textarea
                      name="description"
                      v-model="description"
                      placeholder="Description"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.description }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="role_group_id"
                    class="form-label label text-secondary"
                    >Role Group <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="role_group_id"
                      :options="listRoleGroup"
                      placeholder="Role Group"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.role_group_id }}</span>
                </div>
              </div>
              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
